import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Check, Fingerprint } from "lucide-react";
import { maybeHasSession } from "@/lib/session-hint";
import { pickQuote } from "@/lib/quotes";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ShareButtons } from "@/components/ShareButtons";
import { useReveal } from "@/hooks/use-reveal";
import { Trailer } from "@/components/Trailer";
import { HeroConsole } from "@/components/HeroConsole";
import { DemoDoor } from "@/components/DemoDoor";


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "The Founders App — private operating system for founders" },
      {
        name: "description",
        content:
          "A PIN-locked operating system for every business you run: one move a day, a bold move a month, private journal, contact log and a full history line.",

      },
      {
        property: "og:title",
        content: "The Founders App — private operating system for founders",
      },
      {
        property: "og:description",
        content:
          "One move a day, per business. PIN-locked. Nothing shared, nothing performative, everything remembered.",

      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://thefoundersapp.com/" },
      { property: "og:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      
    ],
    links: [{ rel: "canonical", href: "https://thefoundersapp.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "The Founders App",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          description:
            "A PIN-locked operating system for every business you run: one move a day, a bold move a month, private journal, contact log and a full history line.",
          offers: [
            { "@type": "Offer", name: "Commitment", price: "29", priceCurrency: "USD" },
            { "@type": "Offer", name: "Operator", price: "79", priceCurrency: "USD" },
            { "@type": "Offer", name: "Vault", price: "149", priceCurrency: "USD" },
          ],
          publisher: {
            "@type": "Organization",
            name: "Skylab Studio LLC",
            areaServed: "San Francisco Bay Area, California",
          },
        }),
      },
    ],
  }),
  component: Landing,
});

const INSTRUMENTS = [
  { code: "01", label: "Daily", value: "One move", note: "Set at dawn, closed at dusk." },
  {
    code: "02",
    label: "Monthly",
    value: "One risk",
    note: "Named on the first. Tracked all month.",
  },
  { code: "03", label: "Ledger", value: "Every name", note: "Who you spoke to. What was said." },
  { code: "04", label: "History", value: "Full line", note: "Scroll back. Read the pattern." },
];

const TICKER = [
  "One move a day",
  "Four-digit lock",
  "No streaks",
  "Per-business spaces",
  "Monthly bold move",
  "Private journal",
  "Contact interaction log",
  "Full history line",
  "Nothing shared",
];

const STEPS = [
  {
    code: "01",
    title: "Name the move",
    body: "Morning, per business: one thing that pushes it forward. Tag it — outreach, pricing, product, ops.",
  },
  {
    code: "02",
    title: "Close the day",
    body: "Evening: did it happen, and what came of it. Two lines. No dashboards to feed.",
  },
  {
    code: "03",
    title: "Bet once a month",
    body: "On the first, each business asks for one bold move. Stored apart, tagged as a monthly risk.",
  },
  {
    code: "04",
    title: "Read the line",
    body: "Tasks, outcomes, risks, journal and calls merge into one timeline you can scroll back through.",
  },
];

function Landing() {
  const [quote, setQuote] = useState<ReturnType<typeof pickQuote> | null>(null);
  const [signedIn, setSignedIn] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  useReveal();

  useEffect(() => {
    setQuote(pickQuote());
    setSignedIn(maybeHasSession());
  }, []);

  const track = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = panelRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
    el.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
  };

  return (
    <div className="relative min-h-screen w-full max-w-full overflow-hidden">
      <SiteHeader />

      {/* ── Hero ───────────────────────────────────────────── */}
      <main id="main" className="min-w-0 max-w-full">
      <section className="relative w-full max-w-full overflow-hidden">

        <div className="aurora" aria-hidden />
        <div className="light-shafts" aria-hidden />
        <div className="grid-floor" aria-hidden />

        <div className="haze -top-40 -left-32 h-[26rem] w-[26rem]" aria-hidden />
        <div
          className="haze top-40 -right-40 h-[30rem] w-[30rem] [animation-delay:-9s]"
          aria-hidden
        />
        <div
          className="orbit pointer-events-none absolute -top-1/2 left-1/2 h-[80rem] w-[80rem] -translate-x-1/2 rounded-full border border-foreground/[0.05]"
          aria-hidden
        />

        <div className="relative mx-auto grid w-full max-w-6xl grid-cols-[minmax(0,1fr)] gap-12 px-5 pt-16 pb-20 sm:px-10 sm:pt-20 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-16">
          <div className="min-w-0 max-w-full overflow-hidden">
            <p className="rise micro-label [--d:0ms]">Built for the quietly relentless</p>

            <h1 className="mt-7 max-w-full text-[3rem] leading-[0.92] font-light sm:text-8xl">
              <span className="etched rise block font-semibold [--d:40ms]">Move first.</span>
              <span className="rise block text-muted-foreground [--d:80ms]">
                Say less.
                <span className="caret ml-2 inline-block h-[0.62em] w-[3px] translate-y-[0.02em] bg-foreground/70 align-baseline" />
              </span>
            </h1>

            <p className="rise mt-8 w-full max-w-md break-words text-[0.95rem] leading-relaxed text-muted-foreground [--d:100ms]">
              Track your daily moves for every business you run. Set one bold risk each month. Keep
              a private log of your contacts, ideas, and appointments. See your whole history in one
              place. Locked behind a 4-digit PIN — nobody else can see it.
            </p>

            <div className="rise mt-10 flex w-full flex-col items-stretch gap-3 [--d:160ms] sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
              <Link
                to={signedIn ? "/app/today" : "/auth"}
                {...(!signedIn ? { search: { beta: "1" } } : {})}
                className="sheen group inline-flex min-w-0 items-center justify-center gap-2 rounded-sm bg-primary px-3 py-4 text-center text-sm font-semibold tracking-wide text-primary-foreground shadow-[0_20px_50px_-30px_var(--color-foreground)] transition-transform duration-300 hover:-translate-y-0.5 sm:w-auto sm:gap-3 sm:px-7"
              >
                {signedIn ? "Open The Founders App" : "Take a free beta seat"}

                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex min-w-0 items-center justify-center gap-2 rounded-sm border border-border px-3 py-4 text-center text-sm font-medium transition-colors duration-300 hover:border-foreground/40 sm:w-auto sm:px-6"
              >
                See pricing
              </Link>
              <DemoDoor
                label="Try the live demo"
                className="justify-center rounded-sm border-primary/50 bg-primary/10 px-3 py-4 hover:bg-primary/20 sm:w-auto sm:px-6"
              />
            </div>

            <span className="rise mt-6 flex items-center gap-2 text-xs text-muted-foreground [--d:200ms]">
              <Fingerprint className="h-4 w-4" />
              Free during beta — unlimited businesses, one useful note a week keeps it free
            </span>
          </div>

          <div
            ref={panelRef}
            onPointerMove={track}
            className="rise spotlight vault-panel min-w-0 self-start p-1 [--d:220ms]"
          >
            <div className="relative overflow-hidden border-b border-border px-6 py-4">
              <p className="micro-label">Instrumentation</p>
              <div className="hairline scan-rule absolute inset-x-0 bottom-0 [--d:2s]" />
            </div>
            <dl>
              {INSTRUMENTS.map((item, i) => (
                <div
                  key={item.code}
                  className={`group grid grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-x-5 px-6 py-5 transition-colors duration-300 hover:bg-accent/40 ${
                    i > 0 ? "border-t border-border/70" : ""
                  }`}
                >
                  <dt className="col-start-1 row-start-1 text-[0.625rem] tracking-[0.2em] text-muted-foreground/60 tabular-nums transition-colors duration-300 group-hover:text-foreground">
                    <span className="sr-only">{item.label} — </span>
                    {item.code}
                  </dt>
                  <dd className="col-start-2 row-start-1 min-w-0">
                    <span className="micro-label block">{item.label}</span>
                    <span className="mt-1.5 block text-lg font-medium transition-transform duration-500 ease-out group-hover:translate-x-1">
                      {item.value}
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                      {item.note}
                    </span>
                  </dd>
                  <dd className="col-start-3 row-start-1">
                    <ArrowUpRight className="h-3.5 w-3.5 -translate-x-1 text-muted-foreground opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
                  </dd>
                </div>
              ))}
            </dl>

          </div>
        </div>

        <HeroConsole />

        <div className="mx-auto mt-8 flex max-w-3xl flex-col items-center gap-2 px-5 text-center">
          <DemoDoor label="Open the door — try the live demo" />
          <p className="text-xs leading-relaxed text-muted-foreground">
            No signup, no card. A stocked sample account that resets every night.
          </p>
        </div>
      </section>


      {/* ── Ticker ─────────────────────────────────────────── */}
      <div className="relative overflow-hidden border-y border-border/60 py-4">
        <div className="marquee-track">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex shrink-0 items-center" aria-hidden={dup === 1}>
              {TICKER.map((t) => (
                <span key={`${dup}-${t}`} className="micro-label px-7">
                  {t}
                  <span className="ml-7 text-muted-foreground/30">/</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── Trailer ────────────────────────────────────────── */}
      <Trailer />



      {/* ── How it works ───────────────────────────────────── */}
      <section id="how" className="relative scroll-mt-20 overflow-hidden">
        <div className="mx-auto w-full max-w-6xl px-6 py-16 sm:py-24 sm:px-10">
          <p className="reveal micro-label">How it works</p>
          <h2 className="reveal etched mt-5 max-w-2xl text-4xl leading-[1] font-semibold sm:text-5xl [--d:80ms]">
            A minute a day. Then get back to work.
          </h2>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div
                key={s.code}
                className="reveal soft-panel group p-7 transition-transform duration-500 hover:-translate-y-1"
                style={{ ["--d" as string]: `${120 + i * 100}ms` }}
              >
                <span className="etched text-3xl font-semibold">{s.code}</span>
                <h3 className="mt-5 text-base font-medium">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                <div className="hairline mt-6 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Quote band ─────────────────────────────────────── */}
      <section className="relative overflow-hidden border-y border-border/60">
        <div className="aurora [animation-delay:-11s]" aria-hidden />
        <div className="relative mx-auto w-full max-w-4xl px-6 py-16 sm:py-24 text-center sm:px-10">
          <p className="reveal micro-label">Today's line</p>
          <p className="reveal mt-6 min-h-24 text-2xl leading-snug font-light sm:text-3xl [--d:60ms]">
            {quote && <>“{quote.text}”</>}
          </p>
          {quote && (
            <p className="reveal micro-label mt-5 [--d:200ms]">— {quote.author}</p>
          )}
        </div>
      </section>

      {/* ── Beta seat ──────────────────────────────────────── */}
      <section className="relative overflow-hidden border-y border-border/60">
        <div className="aurora [animation-delay:-16s]" aria-hidden />
        <div className="relative mx-auto w-full max-w-6xl px-6 py-16 sm:py-24 sm:px-10">
          <div className="reveal grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:items-center">
            <div>
              <p className="micro-label">Free beta seat</p>
              <h2 className="reveal etched mt-5 text-3xl font-semibold sm:text-4xl [--d:80ms]">
                Use the full app for free while we build it.
              </h2>
              <p className="reveal mt-5 max-w-md text-sm leading-relaxed text-muted-foreground [--d:160ms]">
                You get Operator-level access: unlimited businesses, daily moves, monthly bold moves,
                journal, phonebook and full history. No card, no auto-charge. Send one useful note a
                week — a bug, a rough edge or an idea — and your seat stays free another 30 days.
                Your seat stays free until we go live; when billing opens, beta testers get a
                founders discount as our thanks for building this with us.
              </p>
            </div>
            <div className="reveal space-y-4 [--d:200ms]">
              {[
                "Every feature unlocked — no watered-down trial.",
                "No credit card and no automatic charges, ever.",
                "One useful note a week — a bug, a rough edge, an idea — keeps your seat active.",
                "Free until we go live — then beta testers get a founders discount.",
                "Your data is always private and exportable.",
              ].map((line) => (
                <div key={line} className="flex items-start gap-3 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span className="text-muted-foreground">{line}</span>
                </div>
              ))}
              <Link
                to="/auth"
                search={{ beta: "1" }}
                className="sheen group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-primary px-6 py-4 text-sm font-semibold tracking-wide text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 sm:w-auto"
              >
                Take a free beta seat
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Closing CTA ────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="mx-auto w-full max-w-6xl px-6 py-16 sm:py-24 sm:px-10">
          <div className="reveal vault-panel flex flex-wrap items-center justify-between gap-8 p-10">
            <div>
              <p className="micro-label">Start tonight</p>
              <p className="etched mt-3 text-3xl font-semibold sm:text-4xl">
                One move. Every business.
              </p>
              <p className="mt-3 max-w-md text-sm text-muted-foreground">
                Join the free beta, lock in your PIN, and name the first move you'll make today.
              </p>
            </div>
            <Link
              to={signedIn ? "/app/today" : "/auth"}
              {...(!signedIn ? { search: { beta: "1" } } : {})}
              className="sheen group inline-flex items-center gap-3 rounded-sm bg-primary px-8 py-4 text-sm font-semibold tracking-wide text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5"
            >
              {signedIn ? "Open The Founders App" : "Take a free beta seat"}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
          <ShareButtons
            className="mt-10 justify-center"
            url="/"
            title="The Founders App — one move a day, per business."
            label="Tell an operator"
          />
        </div>
      </section>
      </main>

      <SiteFooter />

    </div>
  );
}
